import {
  Box,
  ButtonBase,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useMsal } from "@azure/msal-react";

import { LTCPContext } from "../LTCPContext";
import { getDemandChildData } from "../api/ltcpBackend";

import CollapseIcon from "LTCP Module/components/CollapseIcon";
import ExpandIcon from "LTCP Module/components/ExpandIcon";

import { roundOffNumber } from "utils/roundOff";

import {
  DEMAND_HIERARCHIES,
} from "../constants/demandHierarchy";

function roundNumber(value) {
  if (value === null || value === undefined) {
    return 0;
  }

  try {
    return roundOffNumber(value);
  } catch {
    return value;
  }
}

function GrowthCell({ value }) {
  const numericValue = Number(value);

  let backgroundColor = "#ffec9a";

  if (numericValue < 0) {
    backgroundColor = "#ff9998";
  } else if (numericValue > 10) {
    backgroundColor = "#a3e1d2";
  }

  return (
    <TableCell
      sx={{
        backgroundColor,
        textAlign: "right",
        borderBottom: "1px solid #ccc",
        textWrap: "nowrap",
      }}
    >
      {roundNumber(value)}%
    </TableCell>
  );
}

function DemandValueCells({ data, header }) {
  return (
    <>
      {header.map((column) => {
        if (column.startsWith("growth")) {
          return (
            <GrowthCell
              key={column}
              value={data[column]}
            />
          );
        }

        return (
          <TableCell
            key={column}
            sx={{
              textAlign: "right",
              borderBottom: "1px solid #ccc",
              textWrap: "nowrap",
            }}
          >
            {roundNumber(data[column])}
          </TableCell>
        );
      })}

      {data.cagr !== undefined &&
        data.cagr !== null && (
          <GrowthCell value={data.cagr} />
        )}
    </>
  );
}

function getParentPayload(ancestry) {
  const parentPayload = {};

  ancestry.forEach((row) => {
    if (row.branch !== undefined) {
      parentPayload.parentBranch = row.branch;
    }

    if (row.subcategory !== undefined) {
      parentPayload.parentSubcategory =
        row.subcategory;
    }

    if (row.brand !== undefined) {
      parentPayload.parentBrand = row.brand;
    }

    if (row.formatsL1 !== undefined) {
      parentPayload.parentFormatL1 =
        row.formatsL1;
    }

    if (row.formatsL2 !== undefined) {
      parentPayload.parentFormatL2 =
        row.formatsL2;
    }
  });

  return parentPayload;
}

function createRowKey(row, config, level, index) {
  const identifier =
    row[config.key] ??
    row.branch ??
    row.subcategory ??
    row.brand ??
    row.formatsL1 ??
    row.formatsL2 ??
    row.basepackcode ??
    index;

  return `${level}-${identifier}-${index}`;
}

function getHierarchyPath({
  hierarchy,
  ancestry,
  currentData,
  currentLevel,
}) {
  const completePath = [
    ...ancestry,
    currentData,
  ];

  return completePath
    .map((row, pathLevel) => {
      const pathConfig = hierarchy[pathLevel];

      if (!pathConfig) {
        return null;
      }

      const rawValue = row?.[pathConfig.key];

      const formattedValue = pathConfig.formatValue
        ? pathConfig.formatValue(rawValue)
        : rawValue;

      if (
        formattedValue === undefined ||
        formattedValue === null ||
        formattedValue === ""
      ) {
        return null;
      }

      return {
        key: pathConfig.key,
        label: pathConfig.label,
        value: formattedValue,
        isCurrent: pathLevel === currentLevel,
      };
    })
    .filter(Boolean);
}

function HierarchyPath({
  path,
  isColumnExpanded,
}) {
  const completePath = path
    .map((item) => item.value)
    .join(" | ");

  if (!completePath) {
    return null;
  }

  const tooltipSlotProps = {
    tooltip: {
      sx: {
        backgroundColor: "#fff",
        color: "#111",
        border: "1px solid #d0d0d0",
        boxShadow:
          "0 2px 8px rgba(0, 0, 0, 0.18)",
        fontSize: 12,
        fontWeight: 500,
      },
    },

    arrow: {
      sx: {
        color: "#fff",

        "&::before": {
          border: "1px solid #d0d0d0",
        },
      },
    },
  };

  if (!isColumnExpanded) {
    return (
      <Tooltip
        title={completePath}
        placement="top"
        arrow
        slotProps={tooltipSlotProps}
      >
        <Typography
          component="span"
          sx={{
            display: "block",
            width: "100%",
            minWidth: 0,
            color: "#111",
            fontSize: 12,
            lineHeight: 1.4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {completePath}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={completePath}
      placement="top"
      arrow
      slotProps={tooltipSlotProps}
    >
      <Box
        component="span"
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          minWidth: 0,
          color: "#111",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {path.map((item, index) => (
          <Box
            component="span"
            key={`${item.key}-${item.value}-${index}`}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {index > 0 && (
              <Typography
                component="span"
                aria-hidden="true"
                sx={{
                  mx: 1,
                  color: "#666",
                  fontSize: 12,
                }}
              >
                |
              </Typography>
            )}

            <Typography
              component="span"
              sx={{
                color: "#111",
                fontSize: 12,
                lineHeight: 1.4,
                fontWeight:
                  item.isCurrent ? 600 : 400,
                whiteSpace: "nowrap",
              }}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Tooltip>
  );
}

function DemandNestedRow({
  data,
  header,
  tableLevel,
  level = 0,
  filters,
  expandAll,
  ancestry = [],
  onVisibleLevelChange,
  hierarchyColumnWidth,
  isHierarchyColumnExpanded,
}) {
  const { accounts } = useMsal();

  const userEmail = accounts?.[0]?.username;

  const { setLoading } = useContext(LTCPContext);


  const currentAncestry = [...ancestry, data];

  const [expanded, setExpanded] = useState(false);
const [children, setChildren] = useState([]);
const [hasLoaded, setHasLoaded] = useState(false);
const [error, setError] = useState("");

const requestIdRef = useRef(0);
const loadChildrenRef = useRef(null);
const previousExpandAllRef = useRef(expandAll);

const hierarchy = DEMAND_HIERARCHIES[tableLevel];
const levelConfig = hierarchy?.[level];

const isLeaf = Boolean(levelConfig?.isLeaf);

const hierarchyPath = getHierarchyPath({
  hierarchy,
  ancestry,
  currentData: data,
  currentLevel: level,
});


const loadChildren = useCallback(
  async ({ force = false } = {}) => {
    if (
      isLeaf ||
      !userEmail ||
      !levelConfig
    ) {
      return;
    }

    if (hasLoaded && !force) {
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setLoading(true);
    setError("");

    try {
      const parentPayload = getParentPayload(
        // tableLevel,
        [...ancestry, data]
      );

      const response = await getDemandChildData(
        userEmail,
        {
          ...filters,
          ...parentPayload,
          tableLevel,
          level: level + 1,
        }
      );

      if (requestId !== requestIdRef.current) {
        return;
      }

      setChildren(
        Array.isArray(response)
          ? response
          : []
      );

      setHasLoaded(true);
    } catch (loadError) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      console.error(
        "Unable to load nested demand rows:",
        loadError
      );

      setChildren([]);
      setHasLoaded(false);
      setError("Unable to load data");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  },
  [
    ancestry,
    data,
    filters,
    hasLoaded,
    isLeaf,
    level,
    levelConfig,
    setLoading,
    tableLevel,
    userEmail,
  ]
);

/*
 * Keep the latest load function available without making the
 * Expand All effect dependent on the function identity.
 */
useEffect(() => {
  loadChildrenRef.current = loadChildren;
}, [loadChildren]);

const handleToggle = () => {
  if (isLeaf) {
    return;
  }

  if (expanded) {
    setExpanded(false);

    /*
     * Current row remains visible after its children collapse.
     *
     * Example:
     * Branch | Sub-Category | Brand
     *
     * Collapsing Brand removes Format L1 children, so the header
     * should end at Brand.
     */
    onVisibleLevelChange?.(level);

    return;
  }

  setExpanded(true);

  /*
   * Expanding the current row displays its next child level.
   *
   * Example:
   * Expanding Brand displays Format L1, so the header should
   * include Format L1.
   */
  const nextLevel = Math.min(
    level + 1,
    hierarchy.length - 1
  );

  onVisibleLevelChange?.(nextLevel);

  if (!hasLoaded) {
    loadChildren();
  }
};

/*
 * Expand/collapse all should react only to an actual change in
 * the expandAll value. It must not override manual expansion
 * during an unrelated render.
 */
useEffect(() => {
  if (isLeaf) {
    return;
  }

  const previousExpandAll =
    previousExpandAllRef.current;

  previousExpandAllRef.current = expandAll;

  if (!previousExpandAll && expandAll) {
    setExpanded(true);
    loadChildrenRef.current?.();
  }

  if (previousExpandAll && !expandAll) {
    setExpanded(false);
  }
}, [expandAll, isLeaf]);


useEffect(() => {
  if (isLeaf) {
    return;
  }

  const previousExpandAll =
    previousExpandAllRef.current;

  previousExpandAllRef.current = expandAll;

  if (!previousExpandAll && expandAll) {
    setExpanded(true);
    loadChildrenRef.current?.();

    /*
     * Only the root row informs DemandPage.
     * Otherwise every recursive row would update the same state.
     */
    if (level === 0) {
      onVisibleLevelChange?.(
        hierarchy.length - 1
      );
    }
  }

  if (previousExpandAll && !expandAll) {
    setExpanded(false);

    if (level === 0) {
      onVisibleLevelChange?.(0);
    }
  }
}, [
  expandAll,
  hierarchy.length,
  isLeaf,
  level,
  onVisibleLevelChange,
]);

/*
 * Reset only when filters or hierarchy actually change.
 */
useEffect(() => {
  requestIdRef.current += 1;

  setExpanded(false);
  setChildren([]);
  setHasLoaded(false);
  setError("");

  previousExpandAllRef.current = false;
}, [tableLevel, filters.resetKey]);

  if (!levelConfig) {
    return null;
  }



  

  return (
    <>
      <TableRow>
        <TableCell
            sx={{
              pl: 1,
              pr: 1,
              width: hierarchyColumnWidth,
              minWidth: hierarchyColumnWidth,
              maxWidth: hierarchyColumnWidth,
              color: "#111",
              backgroundColor: "#fff",
              borderBottom: "1px solid #ccc",
              fontSize: 12,
              overflow: "hidden",
              whiteSpace: "nowrap",
              transition:
                "width 200ms ease, min-width 200ms ease, max-width 200ms ease",
            }}
          >
            {isLeaf ? (
              <Box
                sx={{
                  width: "100%",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                {/*
                  Placeholder keeps Basepack aligned with rows
                  that contain an expand/collapse icon.
                */}
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    width: 12,
                    minWidth: 12,
                    height: 12,
                    mr: 1,
                    flexShrink: 0,
                  }}
                />

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <HierarchyPath
                    path={hierarchyPath}
                    isColumnExpanded={isHierarchyColumnExpanded}
                    textColor="#111"
                  />
                </Box>
              </Box>
            ) : (
              <ButtonBase
                onClick={handleToggle}
                aria-expanded={expanded}
                aria-label={
                  expanded
                    ? `Collapse ${levelConfig.label}`
                    : `Expand ${levelConfig.label}`
                }
                sx={{
                  width: "100%",
                  minWidth: 0,
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: 1,
                  overflow: "hidden",
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  color: "#111",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  {expanded ? <CollapseIcon /> : <ExpandIcon />}
                </Box>

                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <HierarchyPath
                    path={hierarchyPath}
                    isColumnExpanded={isHierarchyColumnExpanded}
                    textColor="#111"
                  />
                </Box>
              </ButtonBase>
            )}
          </TableCell>

        <DemandValueCells
          data={data}
          header={header}
        />
      </TableRow>
      {expanded && error && (
        <TableRow>
          <TableCell
            colSpan={
              header.length +
              (data.cagr !== undefined ? 2 : 1)
            }
            sx={{
                pl: 4,
                color: "error.main",
                fontSize: 12,
                borderBottom: "1px solid #ccc",
            }}
          >
            {error}
          </TableCell>
        </TableRow>
      )}

      {expanded &&
        !error &&
        children.map((child, index) => {
          const childConfig = hierarchy[level + 1];

          if (!childConfig) {
            return null;
          }

          return (
            <DemandNestedRow
              key={createRowKey(
                child,
                childConfig,
                level + 1,
                index
              )}
              data={child}
              header={header}
              tableLevel={tableLevel}
              level={level + 1}
              filters={filters}
              expandAll={expandAll}
              ancestry={[...ancestry, data]}
              onVisibleLevelChange={onVisibleLevelChange}
              hierarchyColumnWidth={hierarchyColumnWidth}
              isHierarchyColumnExpanded={
                isHierarchyColumnExpanded
              }
            />
          );
        })}
    </>
  );
}

export default memo(DemandNestedRow);